import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Info,
  Undo2,
  X,
} from '@/lib/icons';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

const toastVariants = cva(
  'group pointer-events-auto relative flex w-full items-start gap-3 overflow-hidden rounded-lg border border-border-light bg-card p-4 pr-8 text-foreground shadow-lg transition-all duration-300 ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full',
  {
    variants: {
      variant: {
        default: 'border-l-[3px] border-l-ink-1000',
        success:
          'border-l-[3px] border-l-[hsl(var(--success))]',
        error:
          'border-l-[3px] border-l-[hsl(var(--error))]',
        warning:
          'border-l-[3px] border-l-[hsl(var(--warning))]',
        info: 'border-l-[3px] border-l-[hsl(var(--info))]',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const iconMap = {
  success: CheckCircle,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
  default: Info,
} as const;

const iconContainerMap = {
  success: 'bg-[hsl(var(--success))]',
  error: 'bg-[hsl(var(--error))]',
  warning: 'bg-[hsl(var(--warning))]',
  info: 'bg-[hsl(var(--info))]',
  default: 'bg-ink-1000',
} as const;

export interface ToastProps extends VariantProps<typeof toastVariants> {
  id: string;
  title?: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  undoAction?: {
    label?: string;
    onClick: () => void;
    timeout?: number;
  };
  duration?: number;
  onClose?: () => void;
  persistent?: boolean;
}

interface ToastComponentProps extends ToastProps {
  onDismiss: (id: string) => void;
}

export const Toast = React.forwardRef<HTMLDivElement, ToastComponentProps>(
  (
    {
      id,
      variant = 'default',
      title,
      description,
      action,
      undoAction,
      duration = 5000,
      onClose,
      persistent = false,
      onDismiss,
      className,
      ...props
    },
    ref
  ) => {
    const [isVisible, setIsVisible] = React.useState(true);
    const [undoTimeLeft, setUndoTimeLeft] = React.useState(
      undoAction?.timeout || 5000
    );
    const timeoutRef = React.useRef<NodeJS.Timeout>();
    const undoTimeoutRef = React.useRef<NodeJS.Timeout>();
    const undoIntervalRef = React.useRef<NodeJS.Timeout>();

    const v = variant ?? 'default';
    const Icon = iconMap[v];
    const iconContainer = iconContainerMap[v];

    const handleClose = React.useCallback(() => {
      setIsVisible(false);
      onClose?.();
      setTimeout(() => onDismiss(id), 300);
    }, [id, onClose, onDismiss]);

    const handleUndo = React.useCallback(() => {
      if (undoAction) {
        undoAction.onClick();
        handleClose();
      }
    }, [undoAction, handleClose]);

    React.useEffect(() => {
      if (!persistent && !undoAction) {
        timeoutRef.current = setTimeout(handleClose, duration);
      }

      if (undoAction) {
        const undoTimeout = undoAction.timeout || 5000;

        undoIntervalRef.current = setInterval(() => {
          setUndoTimeLeft((prev) => {
            if (prev <= 100) {
              clearInterval(undoIntervalRef.current);
              return 0;
            }
            return prev - 100;
          });
        }, 100);

        undoTimeoutRef.current = setTimeout(handleClose, undoTimeout);
      }

      return () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        if (undoTimeoutRef.current) clearTimeout(undoTimeoutRef.current);
        if (undoIntervalRef.current) clearInterval(undoIntervalRef.current);
      };
    }, [duration, persistent, undoAction, handleClose]);

    if (!isVisible) return null;

    const progressPercentage = undoAction
      ? (((undoAction.timeout || 5000) - undoTimeLeft) /
          (undoAction.timeout || 5000)) *
        100
      : 0;

    return (
      <div
        ref={ref}
        className={cn(toastVariants({ variant }), className)}
        data-state={isVisible ? 'open' : 'closed'}
        {...props}
      >
        {undoAction && (
          <div className="absolute bottom-0 left-0 h-1 w-full bg-black/10 dark:bg-white/10">
            <div
              className="h-full bg-current transition-all duration-100 ease-linear"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        )}

        <div
          className={cn(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-white',
            iconContainer
          )}
        >
          <Icon className="h-4 w-4" strokeWidth={2.5} />
        </div>

        <div className="min-w-0 flex-1">
          {title && (
            <div className="text-[13px] font-semibold">{title}</div>
          )}
          {description && (
            <div className="mt-0.5 text-xs text-muted-foreground">
              {description}
            </div>
          )}

          {(action || undoAction) && (
            <div className="mt-2.5 flex items-center gap-2">
              {undoAction && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleUndo}
                  className="h-8 gap-1 px-3 text-xs"
                >
                  <Undo2 className="h-3 w-3" />
                  {undoAction.label || 'Undo'}
                  <span className="opacity-60">
                    ({Math.ceil(undoTimeLeft / 1000)}s)
                  </span>
                </Button>
              )}
              {action && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={action.onClick}
                  className="h-8 px-3 text-xs"
                >
                  {action.label}
                </Button>
              )}
            </div>
          )}
        </div>

        <button
          onClick={handleClose}
          className="absolute right-2 top-2 rounded-md p-1 text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus:opacity-100 focus:outline-none focus:ring-2 group-hover:opacity-100"
          aria-label="Close notification"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }
);

Toast.displayName = 'Toast';

interface ToastContainerProps {
  readonly toasts: ToastProps[];
  readonly onDismiss: (id: string) => void;
  readonly maxToasts?: number;
}

export const ToastContainer = ({
  toasts,
  onDismiss,
  maxToasts = 5,
}: Readonly<ToastContainerProps>) => {
  const visibleToasts = toasts.slice(0, maxToasts);

  if (visibleToasts.length === 0) return null;

  return (
    <div className="pointer-events-none fixed right-4 top-4 z-[100] flex w-full max-w-sm flex-col gap-2">
      {visibleToasts.map((toast, index) => (
        <div
          key={toast.id}
          className="pointer-events-auto"
          style={{
            transform: `translateY(${index * 4}px) scale(${1 - index * 0.02})`,
            zIndex: 100 - index,
          }}
        >
          <Toast {...toast} onDismiss={onDismiss} />
        </div>
      ))}
    </div>
  );
};
